import type { Faq as FaqType } from 'libs/http/api/content/content.types';

import type { DeliveryTableRow } from 'ui/organisms/DeliveryTable/DeliveryTable.types';

type Translate = (key: string) => string;

export const getDeliveryRows = (t: Translate): DeliveryTableRow[] => [
  {
    name: t('Bulk snapshot'),
    tag: 'Parquet · CSV · JSONL',
    desc: t(
      'The full corpus, or a subset scoped by country, sector, size or entity type, written to storage you control.',
    ),
    actions: [{ label: t('Request a sample'), href: '/book-a-demo' }],
  },
  {
    name: t('Daily deltas'),
    tag: t('Changed records only'),
    desc: t(
      'Only what changed since the last file, on the same schema and the same keys, so the load is a diff rather than a rebuild.',
    ),
    actions: [{ label: t('Talk to licensing'), href: '#clause' }],
  },
  {
    name: 'API',
    tag: 'REST · JSON',
    desc: t('Instant lookup by identifier, name or address for the paths that have to run live inside your product.'),
    actions: [{ label: t('Read the docs'), href: 'https://api.globaldatabase.com/docs/v2/#setup' }],
  },
  {
    name: t('Cloud and warehouse'),
    tag: 'S3 · GCS · Azure · Snowflake · BigQuery',
    desc: t('Written into infrastructure you already own, with no transfer layer for your team to build or monitor.'),
    actions: [{ label: t('Talk to licensing'), href: '#clause' }],
  },
  {
    name: 'MCP server',
    tag: t('Endpoint'),
    desc: t(
      'Exposes the licensed corpus as tools to Claude and any other Model Context Protocol client, on the same key and the same provenance fields.',
    ),
    actions: [{ label: t('View setup'), href: '/mcp-integration' }],
  },
  {
    name: 'Claude & ChatGPT',
    tag: t('Connector'),
    desc: t(
      'One-click connectors for both assistants. Ask for a company in plain language and get the licensed record back.',
    ),
    actions: [
      { label: t('Add to Claude'), href: 'https://claude.ai/new#directory/connectors/global-database' },
      { label: t('Add to ChatGPT'), href: 'https://chatgpt.com/plugins/plugin_asdk_app_696f807d21a481918a1ed1f43d719ce9' },
    ],
  },
  {
    name: t('Your schema'),
    tag: t('Field mapping'),
    desc: t(
      'Mapped to your field names, your types and your identifiers before the first delivery, so ingestion is a scheduled job rather than a project.',
    ),
    actions: [{ label: t('Talk to licensing'), href: '#clause' }],
  },
];

export const getFaqData = (t: Translate): FaqType[] => [
  {
    id: 'export-data-out-of-product',
    question: t('Can our customers export the data out of our product?'),
    answer: t(
      'Yes, under a redistribution licence. Your users can view, export, download and retain the records you surface, and you set the commercial terms they see. That right sits in the base schedule rather than behind a per-seat surcharge.',
    ),
    default_opened: true,
  },
  {
    id: 'data-when-contract-ends',
    question: t('What happens to the data when the contract ends?'),
    answer: t(
      'Records delivered during the term stay with you. There is no deletion obligation on data already supplied and no certificate to sign. What ends is the flow of new records and updates, which makes the switching decision a commercial one rather than a product outage.',
    ),
  },
  {
    id: 'licensed-to-train-a-model',
    question: t('Are we licensed to use this to train a model?'),
    answer: t(
      'Under a separate written grant, yes. It covers pretraining, fine-tuning, evaluation sets, retrieval indexes and synthetic data generation, scoped so your legal team is not reading silence and guessing. Provenance is retained through the pipeline, so a model output can be traced back to a specific filing.',
    ),
  },
  {
    id: 'licence-limited-to-regions',
    question: t('Is the licence limited to certain regions?'),
    answer: t(
      'The territory is worldwide. Coverage is priced by what you take rather than by where your users happen to sit, so opening a new market does not reopen the contract.',
    ),
  },
  {
    id: 'where-does-data-come-from',
    question: t('Where does the data come from?'),
    answer: t(
      'Directly from government company registries, gazettes and filing authorities across 200+ countries. We have been building those source connections for twelve years. Each field carries the name of the registry it came from, so the chain from your customer’s screen back to the original filing stays unbroken.',
    ),
  },
  {
    id: 'how-fresh-is-a-record',
    question: t('How do we know how fresh a record is?'),
    answer: t(
      'Two dates travel with every field: the date the registry recorded the information, and the date we last retrieved it. Change feeds are synced daily against the registries that publish on a daily cycle, and the retrieval date makes the actual cadence visible per field rather than asserted at file level.',
    ),
  },
  {
    id: 'locked-into-your-identifiers',
    question: t('Will we be locked into your identifiers?'),
    answer: t(
      'No. Every record ships with its native registry identifiers alongside our own keys. If you move, your joins survive, because they were never built on something only we can resolve.',
    ),
  },
  {
    id: 'evaluate-a-sample',
    question: t('Can we evaluate a sample before committing?'),
    answer: t(
      'Yes. We scope a representative sample to the jurisdictions and fields you care about, in the format you intend to ingest, so your team tests the real schema rather than a marketing extract.',
    ),
  },
];
